/**
 * D52 (owner 2026-10-22): a session credential the server drops at the start of a turn (an env name the sandbox
 * refuses, a revoked or deleted keybook entry, a value that was never saved, one that can't be decrypted or is
 * missing a value) is announced to the USER too — not only to the model, whose system message
 * (`credential-resolver.ts`) the chat never shows.
 *
 * The drops ride the turn's `RUN_STARTED.metadata.papan.credentialDrops` (the spec's extension slot, beside
 * `userMessage` — see agui-bridge.ts); the chat dispatches them as `CREDENTIAL_DROPS_EVENT` and the session's
 * credentials hook (`useSessionCredentials`) shows one short notice per drop. Browser-safe: no server imports.
 */

/** Why a session credential was dropped — `ResolveByEntryStatus` (keybook) minus `ok`, plus `no-entry-id`. */
export type CredentialDropReason =
  | 'invalid-env-name'
  | 'access-revoked'
  | 'entry-deleted'
  | 'no-entry-id'
  | 'decrypt-failed'
  | 'value-missing'

/** What the user is told about one dropped credential. Names only — never a value. */
export interface CredentialDropNotice {
  /** The session_credential row (its request id). */
  requestId: string
  /** The credential's label as the popover showed it, when it has one. */
  label?: string
  /** The env var names it was bound to (no `$`). */
  envVars: string[]
  /** A `CredentialDropReason`; an unknown one (a newer server) is announced generically. */
  reason: string
}

/** Drops that DELETE the row (it never comes back by itself); the others keep it, so the user can recover. ONE list
 *  for the server's delete and the hook's local removal. */
export const PERMANENT_CREDENTIAL_DROP_REASONS: ReadonlySet<string> = new Set<CredentialDropReason>([
  'access-revoked',
  'entry-deleted',
  'no-entry-id',
  'invalid-env-name', // the stored mapping never becomes valid
])

/** The window event the chat dispatches with `{ sessionId, drops }` when a turn reports dropped credentials. */
export const CREDENTIAL_DROPS_EVENT = 'papan:session-credentials-dropped'

export interface CredentialDropsEventDetail {
  sessionId: string
  drops: CredentialDropNotice[]
}

/** The drops an AG-UI frame carries (`RUN_STARTED.metadata.papan.credentialDrops`) — [] on a fresh turn whose session
 *  credentials all resolved (review round 2: the hook then forgets what it announced, so a recurrence is said again);
 *  null when the frame carries no report (another frame, a resume, an older server). Defensive: a malformed entry is
 *  skipped, never thrown on. */
export function credentialDropsOf(aev: { type: string; [k: string]: unknown }): CredentialDropNotice[] | null {
  if (aev.type !== 'RUN_STARTED') return null
  const raw = (aev.metadata as { papan?: { credentialDrops?: unknown } } | undefined)?.papan?.credentialDrops
  if (!Array.isArray(raw)) return null
  const out: CredentialDropNotice[] = []
  for (const d of raw as Array<Record<string, unknown>>) {
    if (!d || typeof d.requestId !== 'string' || typeof d.reason !== 'string') continue
    const envVars = Array.isArray(d.envVars) ? d.envVars.filter((v): v is string => typeof v === 'string') : []
    out.push({ requestId: d.requestId, reason: d.reason, envVars, ...(typeof d.label === 'string' && d.label ? { label: d.label } : {}) })
  }
  return out
}
